import React, { useEffect, useState, useCallback } from 'react';
import config from './config';
import { authGetToken, isAuthConfigured } from './auth';
import './VoteWidget.css';

const API_URL = config.apiUrl;

/**
 * Community answer voting for a single question.
 *
 * Logged-in users vote for the option(s) they believe are correct (with an
 * optional note). Votes are held for admin approval; only approved votes are
 * tallied into the percentage bars and shown in the community notes list.
 *
 * Props:
 *   - cert:        certification id (e.g. 'scs-c02')
 *   - questionId:  the question's id
 *   - options:     [{ letter, text }]
 */
export default function VoteWidget({ cert, questionId, options = [] }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tally, setTally] = useState({});
  const [totalVoters, setTotalVoters] = useState(0);
  const [comments, setComments] = useState([]);
  const [myVote, setMyVote] = useState(null);

  // Draft state for the vote form.
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const loggedIn = isAuthConfigured();

  const load = useCallback(async () => {
    if (!loggedIn || !cert || questionId === undefined || questionId === null) return;
    setLoading(true);
    setError(null);
    try {
      const token = await authGetToken();
      if (!token) { setLoading(false); return; }
      const res = await fetch(
        `${API_URL}/api/votes?cert=${encodeURIComponent(cert)}&questionId=${encodeURIComponent(questionId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setTally(data.tally || {});
      setTotalVoters(data.totalVoters || 0);
      setComments(data.approvedComments || []);
      setMyVote(data.myVote || null);
      if (data.myVote) {
        setSelected(data.myVote.selectedAnswers || []);
        setNote(data.myVote.comment || '');
      }
    } catch (err) {
      setError('Could not load community votes.');
    } finally {
      setLoading(false);
    }
  }, [cert, questionId, loggedIn]);

  // Reset + reload whenever the question changes.
  useEffect(() => {
    setOpen(false);
    setJustSubmitted(false);
    setSelected([]);
    setNote('');
    setMyVote(null);
    setTally({});
    setComments([]);
    setTotalVoters(0);
  }, [cert, questionId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggleLetter = (letter) => {
    setSelected((prev) =>
      prev.includes(letter) ? prev.filter((l) => l !== letter) : [...prev, letter]
    );
  };

  const submit = async () => {
    if (selected.length === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = await authGetToken();
      const res = await fetch(`${API_URL}/api/votes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ cert, questionId, selectedAnswers: selected, comment: note.trim() }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setMyVote(data.vote || { selectedAnswers: selected, comment: note.trim(), status: 'pending' });
      setJustSubmitted(true);
    } catch (err) {
      setError('Could not submit your vote. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!loggedIn) return null;

  const pct = (letter) => (totalVoters > 0 ? Math.round(((tally[letter] || 0) / totalVoters) * 100) : 0);
  const sortedLetters = options.map((o) => o.letter);

  return (
    <div className="vote-widget">
      <button
        type="button"
        className="vote-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        🗳️ Community answers {totalVoters > 0 ? `(${totalVoters})` : ''}
        <span className="vote-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="vote-body">
          {loading && <div className="vote-loading">Loading…</div>}
          {error && <div className="vote-error">{error}</div>}

          {!loading && (
            <>
              {/* Approved tally */}
              <div className="vote-tally">
                <div className="vote-tally-head">
                  {totalVoters > 0
                    ? `What ${totalVoters} ${totalVoters === 1 ? 'voter believes' : 'voters believe'} is correct`
                    : 'No approved community votes yet — be the first to vote.'}
                </div>
                {totalVoters > 0 && sortedLetters.map((letter) => (
                  <div key={letter} className="vote-bar-row">
                    <span className="vote-bar-letter">{letter}</span>
                    <div className="vote-bar-track">
                      <div className="vote-bar-fill" style={{ width: `${pct(letter)}%` }} />
                    </div>
                    <span className="vote-bar-pct">{pct(letter)}%</span>
                  </div>
                ))}
              </div>

              {/* Vote form */}
              <div className="vote-form">
                <div className="vote-form-label">
                  {myVote ? 'Update your vote' : 'Vote for the correct answer(s)'}
                </div>
                <div className="vote-chips">
                  {sortedLetters.map((letter) => (
                    <button
                      key={letter}
                      type="button"
                      className={`vote-chip ${selected.includes(letter) ? 'active' : ''}`}
                      onClick={() => toggleLetter(letter)}
                    >
                      {letter}
                    </button>
                  ))}
                </div>
                <textarea
                  className="vote-note"
                  placeholder="Optional: explain your reasoning (shown publicly once approved)…"
                  value={note}
                  maxLength={1000}
                  onChange={(e) => setNote(e.target.value)}
                />
                <div className="vote-actions">
                  <button
                    type="button"
                    className="vote-submit"
                    disabled={selected.length === 0 || submitting}
                    onClick={submit}
                  >
                    {submitting ? 'Submitting…' : myVote ? 'Update vote' : 'Submit vote'}
                  </button>
                  {(justSubmitted || myVote?.status) && (
                    <span className={`vote-status vote-status-${myVote?.status || 'pending'}`}>
                      {myVote?.status === 'approved'
                        ? '✓ Your vote is approved'
                        : myVote?.status === 'rejected'
                        ? '✕ Your vote was not approved'
                        : '⏳ Pending review'}
                    </span>
                  )}
                </div>
              </div>

              {/* Approved community notes */}
              {comments.length > 0 && (
                <div className="vote-comments">
                  <div className="vote-comments-head">Community notes</div>
                  {comments.map((c, i) => (
                    <div key={i} className="vote-comment">
                      <div className="vote-comment-meta">
                        <span className="vote-comment-letters">{(c.selectedAnswers || []).join(', ')}</span>
                        <span className="vote-comment-email">{c.email}</span>
                      </div>
                      <div className="vote-comment-text">{c.comment}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
